<template>
  <div class="parking-subsystem">
    <div v-if="selectedParkingLot && parkingLots.length > 1" class="parking-subsystem__toolbar">
      <span class="parking-subsystem__current">
        <i class="el-icon-location-outline" />
        {{ selectedParkingLot.parkingLotName }}
      </span>
      <el-button size="small" icon="el-icon-sort" @click="selectionVisible = true">切换停车场</el-button>
    </div>
    <subsystem-iframe-shell
      v-if="selectedParkingLot"
      :key="`${currentManagerId}:${selectedParkingLot.parkingLotId}`"
      external-target="ai2park"
      use-parking-lot-id
      :parking-lot-id="selectedParkingLot.parkingLotId"
      platform-name="极派信息科技"
      frame-title="停车系统"
    />
    <div v-else class="parking-subsystem__empty">
      <el-empty :description="emptyDescription" :image-size="100">
        <el-button v-if="parkingLots.length > 1" type="primary" size="small" @click="selectionVisible = true">
          选择停车场
        </el-button>
      </el-empty>
    </div>
    <el-dialog
      title="选择停车场"
      :visible.sync="selectionVisible"
      width="440px"
      custom-class="parking-lot-selection-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <p class="parking-subsystem__hint">请选择要进入的停车场</p>
      <div class="parking-subsystem__options">
        <button
          v-for="lot in parkingLots"
          :key="lot.parkingLotId"
          type="button"
          class="parking-subsystem__option"
          :class="{ 'is-selected': selectedParkingLotId === lot.parkingLotId }"
          @click="selectParkingLot(lot)"
        >
          <span class="parking-subsystem__icon"><i class="el-icon-truck" /></span>
          <span class="parking-subsystem__name">{{ lot.parkingLotName }}</span>
          <i :class="selectedParkingLotId === lot.parkingLotId ? 'el-icon-circle-check' : 'el-icon-arrow-right'" />
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import SubsystemIframeShell from './SubsystemIframeShell.vue'
import { mapGetters } from 'vuex'

const FIRST_REFRESH_FLAG = 'parkingSubsystem:first-enter-refreshed'
const FALLBACK_WINDOW_FLAG = '__parkingSubsystemFirstEnterRefreshed__'

export default {
  name: 'ParkingSubsystem',
  components: { SubsystemIframeShell },
  data () {
    return {
      selectedParkingLotId: '',
      selectionVisible: false,
      isPageActive: true
    }
  },
  computed: {
    ...mapGetters(['currentManagerId', 'managerList']),
    currentManager () {
      const list = Array.isArray(this.managerList) ? this.managerList : []
      return list.find(manager => String(manager.managerId) === String(this.currentManagerId)) || null
    },
    parkingLots () {
      const list = this.currentManager && this.currentManager.parkingLotList
      if (!Array.isArray(list)) return []
      // 停车场 ID 是长整数字符串，始终保留字符串，避免精度丢失。
      return list.filter(lot => lot && lot.parkingLotId != null && String(lot.parkingLotId).trim())
        .map(lot => ({
          parkingLotId: String(lot.parkingLotId).trim(),
          parkingLotName: String(lot.parkingLotName || '停车场').trim()
        }))
    },
    parkingContext () {
      return { managerId: this.currentManagerId, lots: this.parkingLots }
    },
    selectedParkingLot () {
      return this.parkingLots.find(lot => lot.parkingLotId === this.selectedParkingLotId) || null
    },
    emptyDescription () {
      if (!this.currentManager) return '正在加载停车场信息…'
      return this.parkingLots.length ? '请选择要进入的停车场' : '当前管理方暂未配置停车场'
    }
  },
  watch: {
    parkingContext: {
      immediate: true,
      deep: true,
      handler () {
        this.resetParkingSelection()
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    next((vm) => vm.triggerFirstEnterRefresh())
  },
  activated () {
    this.isPageActive = true
    this.resetParkingSelection()
    // keep-alive 场景下首次进入也要兜底一次
    this.triggerFirstEnterRefresh()
  },
  deactivated () {
    this.isPageActive = false
    this.selectionVisible = false
  },
  beforeDestroy () {
    this.selectionVisible = false
  },
  methods: {
    resetParkingSelection () {
      this.selectedParkingLotId = this.parkingLots.length === 1 ? this.parkingLots[0].parkingLotId : ''
      this.selectionVisible = this.isPageActive && this.parkingLots.length > 1
    },
    selectParkingLot (lot) {
      if (!this.parkingLots.some(item => item.parkingLotId === lot.parkingLotId)) return
      this.selectedParkingLotId = lot.parkingLotId
      this.selectionVisible = false
    },
    triggerFirstEnterRefresh () {
      // 仅针对停车系统：首次进入时自动刷新一次（每个会话一次）
      try {
        if (window && window[FALLBACK_WINDOW_FLAG]) return
        const existed = sessionStorage.getItem(FIRST_REFRESH_FLAG)
        if (existed) return

        sessionStorage.setItem(FIRST_REFRESH_FLAG, '1')
        window[FALLBACK_WINDOW_FLAG] = true

        // 放到下一帧，避免导航过程中 reload 被吞
        this.$nextTick(() => {
          setTimeout(() => {
            window.location.reload()
          }, 50)
        })
      } catch (e) {
        // sessionStorage 不可用时用 window 变量兜底（同样只刷新一次）
        if (window && !window[FALLBACK_WINDOW_FLAG]) {
          window[FALLBACK_WINDOW_FLAG] = true
          setTimeout(() => window.location.reload(), 50)
        }
      }
    }
  }
}
</script>

<style scoped>
.parking-subsystem {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.parking-subsystem > .subsystem-iframe-page {
  flex: 1;
  height: auto;
  min-height: 0;
}

.parking-subsystem__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
}

.parking-subsystem__current {
  min-width: 0;
  font-size: 14px;
  color: #303133;
  overflow-wrap: anywhere;
}

.parking-subsystem__current i {
  color: #409eff;
  margin-right: 6px;
}

.parking-subsystem__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.parking-subsystem__hint {
  margin: 0 0 16px;
  color: #909399;
  font-size: 14px;
}

.parking-subsystem__options {
  display: grid;
  gap: 12px;
  max-height: 50vh;
  overflow-y: auto;
}

.parking-subsystem__option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  background: #fff;
  color: #606266;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.parking-subsystem__option:hover,
.parking-subsystem__option:focus-visible,
.parking-subsystem__option.is-selected {
  border-color: #409eff;
  background: #ecf5ff;
  color: #409eff;
}

.parking-subsystem__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #ecf5ff;
  color: #409eff;
  font-size: 22px;
}

.parking-subsystem__name {
  flex: 1;
  min-width: 0;
  color: #303133;
  line-height: 1.6;
  overflow-wrap: anywhere;
}
</style>

<style>
.parking-lot-selection-dialog {
  max-width: calc(100vw - 32px);
  border-radius: 14px;
}
.parking-lot-selection-dialog .el-dialog__body {
  padding-top: 10px;
}
</style>
